import {
    Product,
    Subscription,
    getProducts,
    getCurrentUserSubscriptions,
    createCheckoutSession,
    SessionCreateParams
} from "@stripe/firestore-stripe-payments"
import { useState, useEffect, useMemo } from "react"
import { createPortalLink } from "../services/firebase/functions"
import { ANNUAL_PRICE_ID, MONTHLY_PRICE_ID, PROMO_CODES, paymentsInstance } from "../services/stripe"
import { useUser } from "./useUser"
import toast from "react-hot-toast"
import { getFirebaseAnalytics } from "../services/firebase"

import { useAnalytics } from "./useAnalytics"

// price_1MTtbtGOGhxZLaYPjdmMJVbS => 4.99/mo price
// price_1MV4qBGOGhxZLaYPE7kpRuvM => 7.99/mo price

export const STRIPE_APPSUMO_PRICE_ID = "price_1MV4RuGOGhxZLaYPPbGTdIX7"
export function usePaymentsData() {
    const { user } = useUser()
    const { logEvent } = useAnalytics()
    // for now: only product here will be the premium plan with only one price (monthly)

    const [stripeProductsByPlan, setStripeProductsByPlan] = useState<{ [key: string]: Product }>({})
    const [currentUserSubscription, setCurrentUserSubscription] = useState<Subscription | null | undefined>(undefined)
    useEffect(() => {
        ;(async () => {
            if (!user || stripeProductsByPlan["basic"]) return
            const products = await getProducts(paymentsInstance, {
                includePrices: true,
                activeOnly: true
            })

            const productsByPlan = products.reduce((acc, p) => {
                p.role && (acc[p.role] = p)
                return acc
            }, {} as { [key: string]: Product })
            setStripeProductsByPlan(productsByPlan)
        })()

        // load subscription
        ;(async () => {
            if (!user || currentUserSubscription !== undefined || currentUserSubscription) return
            try {
                const subscription = await getCurrentUserSubscriptions(paymentsInstance, {
                    status: "active"
                })
                setCurrentUserSubscription(subscription?.length > 0 ? subscription[0] : null)
            } catch (e) {
                console.error(`error while retrieving current user subscription : ${e}`)
            }
        })()
    }, [user])

    const [loadingCheckout, setLoadingCheckout] = useState(false)
    /**
     * Redirect to a stripe checkout session for a given plan.
     * @param planPriceId
     * @param payment if true, will redirect to the payment page instead of the subscription page
     */
    const startCheckoutSession = async (
        planPriceId: string,
        payment?: boolean,
        redirect = true,
        promoCode: string | null = null
    ) => {
        console.log("starting checkout session for plan: ", planPriceId, " for user:", user?.uid)
        setLoadingCheckout(true)

        const analytics = await getFirebaseAnalytics()

        // find corresponding price across all products
        const price = Object.values(stripeProductsByPlan).reduce((acc, p) => {
            const price = p.prices.find((pr) => pr.id === planPriceId)
            if (price) {
                acc = price
            }
            return acc
        }, null as any)

        // find plan name
        const planName = Object.keys(stripeProductsByPlan).find((k) => stripeProductsByPlan[k].role === price?.role)

        // if (!user) throw new Error("User is not logged in")
        if (!planPriceId) throw new Error("No plan price id provided")
        if (loadingCheckout) {
            console.log("already loading checkout")
            return
        }

        try {
            const sessionParams: SessionCreateParams = {
                price: planPriceId,
                success_url: `${window.location.origin}/videos/create?purchased=true`,
                mode: payment ? "payment" : "subscription",
                // allow promo codes
                allow_promotion_codes: true
            }
            if (promoCode && promoCode.startsWith("promo_")) {
                sessionParams["promotion_code"] = promoCode
            } else if (window.localStorage.getItem("promoCode")) {
                sessionParams["promotion_code"] = window.localStorage.getItem("promoCode") as string
            }
            const session = await createCheckoutSession(paymentsInstance, sessionParams)

            if (redirect) {
                logEvent("begin_checkout", {
                    plan: planName,
                    currency: price?.currency || "usd",
                    // todo: CHANGE IF PLAN CHANGES
                    value: 0,
                    items: price?.id
                        ? [
                              {
                                  item_id: price?.id,
                                  item_name: planName
                              }
                          ]
                        : undefined
                })
                window.location.assign(session.url)
            } else {
                console.log("session url:", session.url)
                return session.url
            }
        } catch (e) {
            toast.error("Couldn't start checkout session... Please reload and try again.")
            console.error(e)
        }
        // setLoadingCheckout(false)
    }

    const [loadingDashboard, setLoadingDashboard] = useState(false)
    const redirectToStripeDashboard = async () => {
        setLoadingDashboard(true)
        const { data } = await createPortalLink({
            returnUrl: window.location.origin,
            locale: "auto" // Optional, defaults to "auto"
        })
        setLoadingDashboard(false)
        // @ts-ignore
        window.location.assign(data.url)
    }

    const basicPrice = useMemo(
        () => stripeProductsByPlan["basic"]?.prices.find((p) => p.active && p.id === MONTHLY_PRICE_ID),
        [stripeProductsByPlan]
    )

    const annualPrice = useMemo(
        // price_1MV4SnGOGhxZLaYPtJGVNW9o => annual 76.70/y price
        () => stripeProductsByPlan["basic"]?.prices.find((p) => p.active && p.id === ANNUAL_PRICE_ID),
        [stripeProductsByPlan]
    )

    const premiumPrice = useMemo(
        () => stripeProductsByPlan["premium"]?.prices.find((p) => p.active),
        [stripeProductsByPlan]
    )
    const appsumoPrice = useMemo(
        // price_1MV4RuGOGhxZLaYPPbGTdIX7 => annual 76.70/y price
        () => stripeProductsByPlan["basic"]?.prices.find((p) => p.active && p.id === STRIPE_APPSUMO_PRICE_ID),
        [stripeProductsByPlan]
    )

    return {
        redirectToStripeDashboard,
        loadingDashboard,
        startCheckoutSession,
        loadingCheckout,
        premiumPlanProduct: stripeProductsByPlan["premium"],

        // todo: rename to business, or unlimited plan
        basicPlanProduct: stripeProductsByPlan["basic"],
        premiumPrice,
        annualPrice,
        basicPrice,
        appsumoPrice,
        currentUserSubscription
    }
}
